import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package2, Truck, DollarSign, MessageSquare, Settings } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';

const SupplierPortal: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  const stats = [
    {
      title: 'Active Products',
      value: '24',
      icon: <Package2 className="h-6 w-6 text-blue-600" />,
      change: '+12%'
    },
    {
      title: 'Pending Orders',
      value: '8',
      icon: <Truck className="h-6 w-6 text-yellow-600" />,
      change: '-3%'
    },
    {
      title: 'Monthly Revenue',
      value: '$12,450',
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      change: '+18%'
    },
    {
      title: 'New Messages',
      value: '5',
      icon: <MessageSquare className="h-6 w-6 text-purple-600" />,
      change: '0'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Supplier Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/supplier/profile')}
              >
                <Settings className="h-5 w-5 mr-2" />
                Settings
              </Button>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                  {user?.email?.[0].toUpperCase()}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {user?.email}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.title}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="mt-1 text-3xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                  {stat.icon}
                </div>
                <div className="mt-4">
                  <span className={`text-sm ${
                    stat.change.startsWith('+') ? 'text-green-600' : 
                    stat.change.startsWith('-') ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.change} from last month
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                No recent orders to display
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Inventory Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                No inventory alerts at this time
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Product Catalog</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                No products added yet. Click the button below to add your first product.
              </div>
              <div className="flex justify-center">
                <Button onClick={() => navigate('/supplier/products/new')}>
                  <Package2 className="h-4 w-4 mr-2" />
                  Add New Product
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SupplierPortal;